import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Stack,
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { FP } from 'components/financepro';
import { obtenerPlantilla } from '../facturapos/PlantillasFactura';

const CONFIGURACION_DEFAULT = {
  plantillaPorDefecto: '80mm-basica',
  leyendaPiePagina: '',
  mensajeGracias: '',
  nombreEmpresa: '',
  mostrarObservaciones: true,
};

/**
 * Adapta los datos del conduce al formato que esperan las plantillas de factura
 * (PlantillasFactura.jsx). Así reutilizamos los mismos formatos sin modificar ese archivo.
 */
const conduceDataToFacturaData = (conduceData, configuracion) => {
  const {
    items = [],
    numero = '',
    fecha = '',
    cliente_nombre = '',
    conductor = '',
    direccionenvio = '',
    observaciones = '',
    empresa = '',
    subtotal: subtotalConduce = 0,
    total_itbis: totalItbisConduce = 0,
    total: totalConduce = 0,
  } = conduceData;

  const partesObs = [];
  if (conductor) partesObs.push(`Conductor: ${conductor}`);
  if (direccionenvio) partesObs.push(`Dir. envío: ${direccionenvio}`);
  if (observaciones) partesObs.push(observaciones);
  const observacionesCombinadas = partesObs.join(' | ');

  const itemsFactura = items.map((item) => ({
    id: item.id,
    codigo: item.codigo || '—',
    descripcion: item.descripcion || '—',
    cantidad: parseFloat(item.cantidad || 0),
    precio: parseFloat(item.precio || 0),
    descuento: 0,
    subtotal: parseFloat(item.subtotal || 0),
  }));

  const totalImpuestos = parseFloat(totalItbisConduce) || 0;
  const subtotal = parseFloat(subtotalConduce) || 0;
  const total = parseFloat(totalConduce) || (subtotal + totalImpuestos);

  const empresaObj =
    empresa && typeof empresa === 'object' && !Array.isArray(empresa) ? empresa : null;

  return {
    items: itemsFactura,
    impuestos: totalImpuestos > 0
      ? [{ id: 'itbis', codigo: 'ITBIS', nombre: 'ITBIS', porcentaje: 0, monto: totalImpuestos }]
      : [],
    subtotal,
    totalDescuentos: 0,
    totalImpuestos,
    total,
    totalNotaCredito: 0,
    totalAPagar: total,
    documento: 'CONDUCE',
    numero: numero || 'Pendiente',
    fecha,
    ncf: '',
    tipoDocumento: 'CONDUCE',
    observaciones: observacionesCombinadas,
    empresa: empresaObj || empresa,
    cliente_nombre: cliente_nombre || '—',
    configuracion: configuracion || CONFIGURACION_DEFAULT,
    id_empresa: conduceData.id_empresa,
  };
};

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatoFecha = (f) => {
  if (!f) return '—';
  const s = String(f);
  const match = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return s;
};

const labelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: FP.color.textMuted,
  mb: 0.4,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.7rem',
  color: '#FFFFFF',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.navy,
  borderBottom: 'none',
  py: 1.2,
};

/**
 * VistaConduce: presenta el conduce en pantalla con opción de imprimir.
 * Lee los datos de empresa EXCLUSIVAMENTE del contexto (controller), igual
 * que VistaFactura: nombreNegocio, direccion_empresa, rnc_empresa.
 */
const VistaConduce = ({ open, onClose, conduceData = {} }) => {
  const { controller, showError } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    nombreNegocio,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const nombreEmpresaContexto =
    typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '';
  const direccionEmpresaContexto =
    typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
      ? direccionEmpresaCtx.trim()
      : '';
  const rncEmpresaContexto =
    typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';

  const printRef = useRef(null);
  const [configuracion, setConfiguracion] = useState(CONFIGURACION_DEFAULT);
  const [cargandoConfig, setCargandoConfig] = useState(false);

  const {
    items = [],
    numero = '',
    fecha = '',
    cliente_nombre = '',
    conductor = '',
    direccionenvio = '',
    observaciones = '',
    subtotal: subtotalConduce = 0,
    total_itbis: totalItbisConduce = 0,
    total: totalConduce = 0,
  } = conduceData;

  useEffect(() => {
    if (!open) return;
    if (conduceData.configuracion && typeof conduceData.configuracion === 'object') {
      setConfiguracion({ ...CONFIGURACION_DEFAULT, ...conduceData.configuracion });
      setCargandoConfig(false);
      return undefined;
    }
    const idEmpresa = conduceData.id_empresa;
    const dbEmpresa = idEmpresa && typeof idEmpresa === 'object' ? idEmpresa.db_empresa : null;
    if (!dbEmpresa) {
      setConfiguracion(CONFIGURACION_DEFAULT);
      return undefined;
    }
    const nombreTipoTrim = 'CONDUCE';
    const intentos = [nombreTipoTrim, 'Conduce'];

    let cancelado = false;
    setCargandoConfig(true);
    (async () => {
      let configuracionGuardada = null;
      let ultimaRespuesta = null;
      for (const intento of intentos) {
        try {
          const res = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: idEmpresa,
              usuario: usuarioLog || '',
              password: claveLog || '',
              tipo_documento: intento,
            }),
          });
          if (!res.ok) continue;
          const data = await res.json().catch(() => null);
          if (!data) continue;
          if (data.configuracion) ultimaRespuesta = data.configuracion;
          if (data.configuracionGuardada && data.configuracion) {
            configuracionGuardada = data.configuracion;
            break;
          }
        } catch (_) {
          // intento siguiente
        }
      }
      if (cancelado) return;
      const elegida = configuracionGuardada || ultimaRespuesta;
      setConfiguracion(
        elegida ? { ...CONFIGURACION_DEFAULT, ...elegida } : CONFIGURACION_DEFAULT
      );
      setCargandoConfig(false);
    })();

    return () => {
      cancelado = true;
    };
  }, [open, conduceData.configuracion, conduceData.id_empresa, usuarioLog, claveLog, servidor]);

  const configuracionResuelta = {
    ...configuracion,
    nombreEmpresa: nombreEmpresaContexto || configuracion.nombreEmpresa || '',
    direccionEmpresa: direccionEmpresaContexto || configuracion.direccionEmpresa || '',
    rncEmpresa: rncEmpresaContexto || configuracion.rncEmpresa || '',
  };

  const handleImprimir = () => {
    if (cargandoConfig) {
      showError('Cargando configuración de impresión, intente nuevamente en un momento');
      return;
    }
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    const plantillaPorDefecto =
      (configuracionResuelta.plantillaPorDefecto || '').trim() || '80mm-basica';
    const facturaDataAdaptada = conduceDataToFacturaData(conduceData, configuracionResuelta);
    const htmlContent = obtenerPlantilla(plantillaPorDefecto, facturaDataAdaptada);
    ventana.document.write(htmlContent);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          maxHeight: '92vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: FP.color.navySoft,
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.98rem', lineHeight: 1.2 }}>
            Conduce {numero || 'Pendiente'}
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
            Vista previa · Documento de entrega
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<CloseIcon />}
          onClick={onClose}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: FP.color.textSecondary,
            borderRadius: 1.4,
            '&:hover': { bgcolor: FP.color.surfaceAlt },
          }}
        >
          Cerrar
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface }}>
        <Paper
          ref={printRef}
          elevation={0}
          sx={{
            bgcolor: '#FFFFFF',
            border: `1px solid ${FP.color.border}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: 4, bgcolor: FP.color.navy }} />
          <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
            {/* Encabezado */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={7}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: FP.color.navy,
                    fontSize: '1.35rem',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                    mb: 0.6,
                  }}
                >
                  {configuracionResuelta.nombreEmpresa || 'Empresa'}
                </Typography>
                {configuracionResuelta.direccionEmpresa && (
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mb: 0.3 }}>
                    {configuracionResuelta.direccionEmpresa}
                  </Typography>
                )}
                {configuracionResuelta.rncEmpresa && (
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mb: 0.3 }}>
                    RNC: {configuracionResuelta.rncEmpresa}
                  </Typography>
                )}
                {configuracionResuelta.telefonoEmpresa && (
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mb: 0.3 }}>
                    Tel: {configuracionResuelta.telefonoEmpresa}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={5}>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography sx={labelSx}>Documento</Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: FP.color.navy,
                      letterSpacing: '0.12em',
                      fontSize: '0.95rem',
                      mb: 0.4,
                    }}
                  >
                    CONDUCE
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: FP.color.textPrimary,
                      fontSize: '1.35rem',
                      fontVariantNumeric: 'tabular-nums',
                      mb: 0.4,
                    }}
                  >
                    Nº {numero || 'Pendiente'}
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                    Fecha: {formatoFecha(fecha)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Bloque cliente */}
            <Box
              sx={{
                mb: 2.5,
                p: 2,
                borderRadius: 1.5,
                bgcolor: FP.color.surfaceAlt,
                border: `1px solid ${FP.color.border}`,
              }}
            >
              <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 1 }}>Cliente</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem', mb: 0.4 }}>
                    {cliente_nombre || '—'}
                  </Typography>
                  {conductor && (
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mt: 0.3 }}>
                      Conductor: {conductor}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {direccionenvio && (
                    <>
                      <Typography sx={labelSx}>Entrega / Envío</Typography>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                        {direccionenvio}
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
              {observaciones && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={labelSx}>Observaciones</Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>
                    {observaciones}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Detalle */}
            <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 1 }}>Detalle</Typography>
            <TableContainer sx={{ borderRadius: 1.5, border: `1px solid ${FP.color.border}`, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={cellHeadSx}>Código</TableCell>
                    <TableCell sx={cellHeadSx}>Descripción</TableCell>
                    <TableCell align="center" sx={cellHeadSx}>Cant.</TableCell>
                    <TableCell align="center" sx={cellHeadSx}>Unidad</TableCell>
                    <TableCell align="right" sx={cellHeadSx}>Precio</TableCell>
                    <TableCell align="right" sx={cellHeadSx}>Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: FP.color.textMuted, py: 3 }}>
                        Sin ítems
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, idx) => (
                      <TableRow key={item.id || idx} sx={{ bgcolor: idx % 2 === 0 ? '#FFFFFF' : FP.color.surfaceAlt }}>
                        <TableCell sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                          {item.codigo || '—'}
                        </TableCell>
                        <TableCell sx={{ color: FP.color.textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>
                          {item.descripcion || '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                          {parseFloat(item.cantidad || 0)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.85rem', color: FP.color.textSecondary }}>
                          {item.unidad || 'UND'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtMoney(item.precio)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtMoney(item.subtotal)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totales */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
              <Box
                sx={{
                  minWidth: 260,
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                }}
              >
                <Stack spacing={0}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, bgcolor: FP.color.surfaceAlt }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: FP.color.textSecondary }}>
                      Subtotal:
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: FP.color.textPrimary }}>
                      ${fmtMoney(subtotalConduce)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: FP.color.textSecondary }}>
                      ITBIS:
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums', color: FP.color.textSecondary }}>
                      ${fmtMoney(totalItbisConduce)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.2,
                      bgcolor: FP.color.navy,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#FFFFFF' }}>
                      Total:
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: '#FFFFFF' }}>
                      ${fmtMoney(totalConduce)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                mt: 3,
                pt: 1.5,
                borderTop: `1px solid ${FP.color.border}`,
                textAlign: 'center',
              }}
            >
              {configuracionResuelta.leyendaPiePagina && (
                <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem', mb: 0.3 }}>
                  {configuracionResuelta.leyendaPiePagina}
                </Typography>
              )}
              <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
                Este documento es un conduce de entrega y no constituye una factura fiscal.
              </Typography>
              <Typography sx={{ color: FP.color.textMuted, fontSize: '0.7rem', mt: 0.3 }}>
                Emitido el {formatoFecha(fecha)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          startIcon={<PrintIcon />}
          onClick={handleImprimir}
          variant="outlined"
          disabled={cargandoConfig}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 1.4,
            borderColor: FP.color.border,
            color: FP.color.textPrimary,
            '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            '&.Mui-disabled': { color: FP.color.textMuted },
          }}
        >
          {cargandoConfig ? 'Cargando formato…' : 'Imprimir'}
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            bgcolor: FP.color.navy,
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VistaConduce;
